import { useEffect, useState } from 'react';
import { Package, Loader2, CheckCircle, XCircle, AlertCircle, Calendar, Hash, ExternalLink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { purchasingAPI, GoodsReceiptNote } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface POLineGRNTabProps {
  lineId: number;
  /** CR-4698: Total ordered quantity from the PO line, used for Outstanding calculation. */
  orderedQty?: number;
}

interface GRNSummary {
  total_grns: number;
  total_received: string;
  total_accepted: string;
  total_rejected: string;
  pending_inspection: number;
  grns: GoodsReceiptNote[];
}

export default function POLineGRNTab({ lineId, orderedQty = 0 }: POLineGRNTabProps) {
  const [summary, setSummary] = useState<GRNSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const openGRN = (grnNumber: string) => {
    window.open(`/purchasing?tab=delivery&search=${encodeURIComponent(grnNumber)}`, '_blank');
  };

  const openAllOrdersOnGRN = (grnNumber: string) => {
    const docNumber = grnNumber.split('-')[0];
    window.open(`/purchasing?tab=delivery&search=${encodeURIComponent(docNumber)}`, '_blank');
  };

  useEffect(() => {
    fetchGRNSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchGRNSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getLineGRNSummary(lineId);
      setSummary(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch GRN summary:', err);
      setError(getApiErrorMessage(err, 'Failed to load GRN summary'));
      toast.error('Failed to load GRN summary');
    } finally {
      setLoading(false);
    }
  };

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const getInspectionStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: LucideIcon }> = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
      received: { label: 'Received', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      passed: { label: 'Passed', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      partial: { label: 'Partial', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle },
      completed: { label: 'Completed', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle },
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchGRNSummary}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.total_grns === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground font-medium">No GRNs found for this PO line</p>
        <p className="text-sm text-muted-foreground mt-2">
          Goods Receipt Notes will appear here once deliveries are recorded
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CR-4698: Summary Statistics — Total Ordered, Total Received, Outstanding */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Ordered</span>
            <Package className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{orderedQty.toFixed(0)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Total Received</span>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{parseFloat(summary.total_received).toFixed(0)}</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-900">Outstanding Qty</span>
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-orange-900">{Math.max(0, orderedQty - parseFloat(summary.total_received)).toFixed(0)}</p>
        </div>
      </div>

      {/* GRN List */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4">Delivery History</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">GRN Number</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Receipt Date</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground">Sched Line</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Received</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Accepted</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Rejected</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Inspection Status</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Packing Slip</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {summary.grns.map((grn, index) => (
                <tr
                  key={grn.id}
                  className={`hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <button
                        onClick={() => openGRN(grn.grn_number)}
                        className="font-medium text-primary hover:underline inline-flex items-center gap-1"
                        title="View this GRN on Deliveries page"
                      >
                        {grn.grn_number}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => openAllOrdersOnGRN(grn.grn_number)}
                        className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                        title="View all lines on this delivery"
                      >
                        View All
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatDate(grn.receipt_date)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">{grn.schedule_line_number ?? '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium">{parseFloat(grn.quantity_received).toFixed(0)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-green-700">{parseFloat(grn.quantity_accepted).toFixed(0)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-red-700">{parseFloat(grn.quantity_rejected).toFixed(0)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {parseFloat(grn.quantity_accepted) + parseFloat(grn.quantity_rejected) >= parseFloat(grn.quantity_received)
                      ? getInspectionStatusBadge('completed')
                      : getInspectionStatusBadge(grn.inspection_status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">
                      {grn.packing_slip_number || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Acceptance Rate Summary */}
      {summary.total_grns > 0 && parseFloat(summary.total_received) > 0 && (
        <div className="bg-muted/30 border rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Acceptance Rate</p>
              <p className="text-xl font-bold text-primary">
                {((parseFloat(summary.total_accepted) / parseFloat(summary.total_received)) * 100).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Rejection Rate</p>
              <p className="text-xl font-bold text-red-600">
                {((parseFloat(summary.total_rejected) / parseFloat(summary.total_received)) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
